import React, { createContext, useCallback, useContext, useEffect } from "react";
import {
  QueryClient,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query";

import { getUser } from "generated/api/user";
import { UserMeDTO } from "generated/schemas";

import { userKeys } from "hooks/api/query-keys";
import { ClientAttributes } from "hooks/api/use-clients";

import { Permission, Role } from "types/auth";

export interface UserAttributes {
  id: string;
  client: Partial<ClientAttributes>;
  clients: Partial<ClientAttributes>[];
  roles: Role[];
  permissions: Permission[];
  firstName?: string;
  lastName?: string;
  email: string;
}

const userApi = getUser();

const mapUser = (dto: UserMeDTO): UserAttributes => {
  const rawClients = (dto as UserMeDTO & { clients?: { id: string; name: string }[] }).clients;

  return {
    id: dto.id,
    client: (dto.client ?? {}) as Partial<ClientAttributes>,
    clients: rawClients ? rawClients.map((client) => ({ id: client.id, name: client.name })) : [],
    roles: (dto.roles ?? []) as Role[],
    permissions: (dto.permissions ?? []) as Permission[],
    firstName: dto.firstName,
    lastName: dto.lastName,
    email: dto.email,
  };
};

const CurrentUserContext = createContext<UserAttributes | null>(null);

export const CurrentUserProvider = ({
  user,
  children,
}: React.PropsWithChildren<{ user: UserAttributes }>) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    updateCurrentUserCache(queryClient, user);
  }, [queryClient, user]);

  return <CurrentUserContext.Provider value={user}>{children}</CurrentUserContext.Provider>;
};

export const fetchCurrentUser = async (): Promise<UserAttributes> => {
  const dto = await userApi.getAccount();

  return mapUser(dto);
};

export const useCurrentUserQuery = <TData = UserAttributes>(
  options?: Omit<UseQueryOptions<UserAttributes, Error, TData>, "queryKey" | "queryFn">,
) =>
  useQuery({
    queryKey: userKeys.me(),
    queryFn: fetchCurrentUser,
    ...options,
  });

export function updateCurrentUserCache(queryClient: QueryClient, attributes: UserAttributes) {
  queryClient.setQueryData(userKeys.me(), attributes);
}

export function clearCurrentUserCache(queryClient: QueryClient) {
  queryClient.removeQueries({ queryKey: userKeys.me() });
}

export const useUpdateCurrentUserCache = () => {
  const queryClient = useQueryClient();

  return useCallback((attributes: UserAttributes) => {
    updateCurrentUserCache(queryClient, attributes);
  }, [queryClient]);
};

export const useClearCurrentUserCache = () => {
  const queryClient = useQueryClient();

  return useCallback(() => {
    clearCurrentUserCache(queryClient);
  }, [queryClient]);
};

export function useCurrentUser(): UserAttributes {
  const contextUser = useContext(CurrentUserContext);
  const { data } = useCurrentUserQuery({
    enabled: !contextUser,
  });
  const attributes = data ?? contextUser;

  if (!attributes) {
    throw new Error("useCurrentUser: called before a user was cached");
  }

  return attributes;
}
